/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderUserContext,
  CommunicationEntryPoint,
  EmailDialogConfig,
  emailDialogRoute,
  MeetingDialogConfig,
  meetingDialogRoute,
  SmsDialogConfig,
  smsDialogRoute,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  CommunicationType,
  ContactCustomerType,
  SalesFolderStatus,
  ActivityRestService, ActivityDetails,
} from '@gh/builder-shared-data';
import { BreakpointManager } from '@gh/common-internal-shared';
import { AppRouter, EnumDictionary, getRouteInitData } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService,
  FormMode,
  NotificationService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { currentDate, UTCToLocal } from '@gh/core-util';
import { filter$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';

import {
  LeadBoardCardMoveDialogConfig,
  LeadBoardColumnId,
  LeadBoardListItem,
} from '../../../../resources/lead-board.model';
import { LeadBoardCommandService } from '../../lead-board-command.service';
import { LeadBoardFormState } from '../../lead-board-form.service';

import { createLeadBoardCardMoveDialog } from './lead-board-card-move-dialog/lead-board-card-move-dialog.route';

@Component({
  standalone: false,
  selector: 'gh-lead-board-card',
  templateUrl: 'lead-board-card.component.html',
  styleUrls: ['lead-board-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeadBoardCardComponent implements OnInit {

  @Input() card: LeadBoardListItem;
  @HostBinding('class.m-is-expanded')
  @Input() expanded: boolean;

  @Output() toggle = new EventEmitter<void>();

  isEditMode: boolean;
  isViewMode: boolean;
  statusDict: EnumDictionary<SalesFolderStatus>;
  notProceedingReasons: any[];
  typeOfCustomerDict: EnumDictionary<ContactCustomerType>;
  isMobile$: Observable<boolean>;

  @HostBinding('class.m-is-overdue')
  get isOverdue(): boolean {
    const { followUpDate } = this.card;
    return !!followUpDate && followUpDate < currentDate();
  }

  @HostBinding('class.m-is-editable')
  get isEditable(): boolean {
    return this.isEditMode;
  }

  @HostBinding('class.m-is-dragged')
  get isDragged(): boolean {
    return this.card.isDragged === true;
  }

  @HostBinding('class.m-is-new-lead')
  get isNewLead(): boolean {
    return this.card.stage === 0 /* Stage.New */ && this.card.isNewWebsiteLead;
  }

  @HostBinding('class.m-needs-action')
  get needsAction(): boolean {
    return !this.isNewLead && !this.card.followUpDate;
  }

  get isNotProceedingVisible(): boolean {
    return this.card.status === SalesFolderStatus.WillNotComplete && !!this.card.notProceedingReason;
  }

  get notProceeding(): string {
    return this.notProceedingReasons.find((item) => `${item.id}` === `${this.card.notProceedingReason}`)?.name;
  }

  get followUpDate(): Date | undefined {
    return this.card.followUpDate ? UTCToLocal(this.card.followUpDate) : undefined;
  }

  constructor(private route: ActivatedRoute,
              private breakpoints: BreakpointManager,
              private form: LeadBoardFormState,
              private commandService: LeadBoardCommandService,
              private translationService: TranslationService,
              private notificationService: NotificationService,
              private userContext: BuilderUserContext,
              private router: AppRouter,
              private activityService: ActivityRestService,
              private dialogService: DialogService,
              private cdr: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    const { statusDict, typeOfCustomerDict, notProceedingReasons } = getRouteInitData(this.route);
    this.statusDict = statusDict;
    this.notProceedingReasons = notProceedingReasons;
    this.typeOfCustomerDict = typeOfCustomerDict;
    this.isEditMode = this.userContext.hasPermission(BuilderPermission.Sales.Leads.EDIT);
    this.isViewMode = this.userContext.hasPermission(BuilderPermission.Sales.Leads.VIEW);
    this.isMobile$ = this.breakpoints.observeMatches('mobile');
  }

  isSalesFolderActive(): boolean {
    return this.isViewMode || this.isEditMode;
  }

  isLastQuoteActive(): boolean {
    return this.isViewMode || this.isEditMode;
  }

  isLastQuoteExists(): boolean {
    return !!this.card.lastQuote;
  }

  isAddresseeActive(): boolean {
    return (this.isEditMode && this.userContext.hasPermission(BuilderPermission.Contacts.Customer.CREATE_EDIT))
      || (this.isViewMode && this.userContext.hasPermission(BuilderPermission.Contacts.Customer.VIEW));
  }

  goToContact(): void {
    this.router.navigateToChild([`/contacts`, this.card.contactId], {
      parentTitle: 'lbl_leads',
    });
  }

  goToSalesFolder(): void {
    this.router.navigateToChild(['/sales', 'leads', this.card.contactId], {
      queryParams: {
        salesFolderId: this.card.salesFolderId,
      },
      parentTitle: 'lbl_leads',
    });
  }

  setIsNotNewWebsiteLead(): void {
    this.card.isNewWebsiteLead = false;
    this.cdr.detectChanges();
  }

  goToQuotation(): void {
    this.router.navigateToChild(
      ['/sales', 'sales-folders', this.card.salesFolderId, 'rfq', 'quotations', this.card.lastQuotationId],
      {
        parentTitle: 'lbl_leads',
      });
  }

  getStatusLabel(status: SalesFolderStatus): string {
    switch (status) {
      case SalesFolderStatus.Active:
        return 'lbl_a';
      case SalesFolderStatus.WillNotComplete:
        return 'lbl_nc';
      case SalesFolderStatus.Completed:
        return 'lbl_c';
      default:
        return '';
    }
  }

  onToggleExpand(): void {
    this.toggle.emit(void 0);
  }

  onSendSms(): void {
    this.dialogService.openWithInput(smsDialogRoute, <SmsDialogConfig>{
      entryPoint: CommunicationEntryPoint.Lead,
      contactId: this.card.contactId,
      sms: {
        cellPhone: this.card.contactPhone,
        subject: '',
        message: '',
        communicationType: CommunicationType.OutgoingSms,
        attachments: [],
        contactId: this.card.contactId,
        isMarkedAsRead: true,
      },
    })
    .afterClosed().pipe(
    filter$(Boolean))
    .subscribe(() => {  this.setIsNotNewWebsiteLead(); });
  }

  onSendEmail(): void {
    this.dialogService.openWithInput(emailDialogRoute, <EmailDialogConfig>{
        entryPoint: CommunicationEntryPoint.Lead,
        contactId: this.card.contactId,
        gFolderId: this.card.customerGoogleFolderId,
        email: {
          email: this.card.contactEmail,
          subject: '',
          message: '',
          communicationType: CommunicationType.Email,
          attachments: [],
          contactId: this.card.contactId,
          isMarkedAsRead: true,
        },
      })
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe(() => { this.setIsNotNewWebsiteLead(); });
  }

  onAddMeeting(): void {
    this.dialogService.openWithInput(meetingDialogRoute, <MeetingDialogConfig>{
      entryPoint: CommunicationEntryPoint.LeadBord,
      salesFolderId: this.card.salesFolderId,
      mode: FormMode.CreateMode,
      helpId: 'sales/meetings',
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((updatedActivity: ActivityDetails) => {
        if (updatedActivity.isDone) {
          this.dialogService.confirmation(yesNoConfirmation('msg_activity_marked_as_done')).pipe(
            filter$(yesAnswer))
            .subscribe(() => this.onAddMeeting());
        }
        this.commandService.filterLeadBoardCardsByInfo(() => this.cdr.detectChanges());
      });
  }

  onMoveCard(): void {
    this.dialogService.openWithInput(createLeadBoardCardMoveDialog, <LeadBoardCardMoveDialogConfig>{
        card: this.card,
      })
      .afterClosed()
      .subscribe((moveParams: LeadBoardColumnId) => {
        if (moveParams) {
          this.commandService.dropItem(this.card, moveParams);
          this.cdr.detectChanges();
        }
      });
  }

  onEditActivity(card: LeadBoardListItem): void {
    this.activityService.get(card.salesFolderId, card.earliestActivityId).subscribe((activity) => {
      const isDone = activity.isDone;
      this.dialogService.openWithInput(meetingDialogRoute, <MeetingDialogConfig>{
        entryPoint: CommunicationEntryPoint.LeadBord,
        salesFolderId: this.card.salesFolderId,
        mode: FormMode.EditMode,
        meetingId: card.earliestActivityId,
        meeting: activity,
        helpId: 'sales/meetings',
      }).afterClosed().pipe(
        filter$(Boolean))
        .subscribe((updatedActivity: ActivityDetails) => {
          if (updatedActivity.isDone && !isDone) {
            this.dialogService.confirmation(yesNoConfirmation('msg_activity_marked_as_done')).pipe(
              filter$(yesAnswer))
              .subscribe(() => this.onAddMeeting());
          }
          this.commandService.filterLeadBoardCardsByInfo(() => this.cdr.detectChanges());
        });
    },                                                                              this.notificationService.processAll);

  }
}
